import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Checkbox, Eyebrow, IconButton } from '@/components/ui';
import { useTaskStore } from '@/stores/taskStore';
import { hapticDragDrop, hapticSubtaskCheck } from '@/features/lifecycle/haptics';
import { uuid } from '@/lib/uuid';
import type { Subtask, Task } from '@/types';

interface SortableSubtaskProps {
  sub: Subtask;
  onToggle: () => void;
  onRemove: () => void;
}

// dnd-kit (not Motion Reorder): the KeyboardSensor + focusable handle give
// keyboard/AT users a reorder path the pointer-only Reorder.Group lacked.
// Listeners live on the handle (setActivatorNodeRef), never the row — a
// row-level sensor swallows spacebar in inputs and checkbox toggles.
function SortableSubtask({ sub, onToggle, onRemove }: SortableSubtaskProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sub.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 can-hover:hover:bg-rail"
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${sub.title}`}
        className="cursor-grab touch-none rounded-sm text-t3 active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
      >
        <GripVertical className="h-3 w-3" aria-hidden />
      </button>
      <Checkbox checked={sub.done} onCheckedChange={onToggle} aria-label={sub.title} />
      {/* min-w-0 + break-words: a long unbroken title wraps inside the row
          instead of forcing the list into horizontal overflow. */}
      <span
        className={cn(
          'min-w-0 flex-1 break-words text-body',
          sub.done && 'opacity-50 line-through',
        )}
      >
        {sub.title}
      </span>
      <IconButton
        label="Remove subtask"
        size="sm"
        variant="ghost"
        icon={<X className="h-3 w-3" />}
        onClick={onRemove}
        className="opacity-100 can-hover:opacity-0 can-hover:group-hover:opacity-100 can-hover:focus-visible:opacity-100"
      />
    </li>
  );
}

export function SubtaskList({ task }: { task: Task }) {
  const updateTask = useTaskStore((s) => s.updateTask);
  const [draft, setDraft] = useState('');

  const sorted = useMemo(
    () => [...task.subtasks].sort((a, b) => a.order - b.order),
    [task.subtasks],
  );
  const doneCount = sorted.filter((s) => s.done).length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onToggle = (subId: string) => {
    void hapticSubtaskCheck();
    updateTask(task.id, {
      subtasks: task.subtasks.map((s) => (s.id === subId ? { ...s, done: !s.done } : s)),
    });
  };

  const onRemove = (subId: string) => {
    updateTask(task.id, { subtasks: task.subtasks.filter((s) => s.id !== subId) });
  };

  const onAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const newSub: Subtask = {
      id: uuid(),
      title: trimmed,
      done: false,
      order: task.subtasks.length,
    };
    updateTask(task.id, { subtasks: [...task.subtasks, newSub] });
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <Eyebrow className="mb-1 tabular-nums">
        Subtasks ({doneCount}/{sorted.length})
      </Eyebrow>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => {
          const { active, over } = e;
          if (!over || active.id === over.id) return;
          const oldIdx = sorted.findIndex((s) => s.id === active.id);
          const newIdx = sorted.findIndex((s) => s.id === over.id);
          if (oldIdx < 0 || newIdx < 0) return;
          const reordered = arrayMove(sorted, oldIdx, newIdx).map((s, i) => ({ ...s, order: i }));
          updateTask(task.id, { subtasks: reordered });
          void hapticDragDrop();
        }}
      >
        <SortableContext items={sorted.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <ul role="list" aria-label="Subtasks" className="space-y-1">
            {sorted.map((sub) => (
              <SortableSubtask
                key={sub.id}
                sub={sub}
                onToggle={() => onToggle(sub.id)}
                onRemove={() => onRemove(sub.id)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <div className="mt-2 flex items-center gap-2 px-2">
        <Plus className="h-3 w-3 text-t3" aria-hidden />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add subtask…"
          aria-label="Add subtask"
          // Names aren't prose — no spellcheck squiggles under subtask titles.
          spellCheck={false}
          className="flex-1 rounded-md bg-transparent text-body text-t1 outline-none placeholder:text-t3 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        />
      </div>
    </div>
  );
}
